import { useToast, useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { PromptDTO } from "@shaarp/common";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

type CalendarType = { id: string; name: string; primary?: boolean; accessRole?: string };

type CalendarIntegration = {
  readonly promptId?: string | null;
  readonly calendarId?: string | null;
  readonly selectedCalendarId?: string | null;
};

type CalendarIntegrationConfig = {
  readonly integration: CalendarIntegration | null;
  readonly apiBasePath: string;
  readonly oauthEnvKey: string;
  readonly oauthUrl: string;
  readonly oauthParams: (clientId: string) => Record<string, string>;
  readonly refetch: () => Promise<void>;
  readonly i18nPrefix: string;
  readonly syncSuccessI18nKey?: string;
  readonly refetchAfterSync?: boolean;
};

type UseCalendarIntegrationPageReturn<T extends CalendarType> = {
  readonly calendars: T[];
  readonly isLoadingCalendars: boolean;
  readonly isDisconnecting: boolean;
  readonly showDisconnectDialog: boolean;
  readonly setShowDisconnectDialog: (v: boolean) => void;
  readonly showPromptDialog: boolean;
  readonly setShowPromptDialog: (v: boolean) => void;
  readonly showCreatePromptDialog: boolean;
  readonly setShowCreatePromptDialog: (v: boolean) => void;
  readonly isUpdatingPrompt: boolean;
  readonly promptName: string | null;
  readonly isSyncing: boolean;
  readonly isCreatingFakeEvent: boolean;
  readonly showFakeEventDialog: boolean;
  readonly setShowFakeEventDialog: (v: boolean) => void;
  readonly fetchCalendars: () => Promise<void>;
  readonly handleConnect: () => void;
  readonly handleDisconnect: () => Promise<void>;
  readonly handleUpdateCalendar: (calendarId: string) => Promise<void>;
  readonly handleUpdatePrompt: (prompt: PromptDTO | null) => Promise<void>;
  readonly handleSync: () => Promise<void>;
  readonly handleCreateFakeEvent: (emails: string[]) => Promise<void>;
};

/**
 * Shared hook encapsulating state and handlers for calendar integration pages.
 * Used by both Google Calendar and Outlook Calendar pages.
 */
export function useCalendarIntegrationPage<T extends CalendarType>(
  config: CalendarIntegrationConfig
): UseCalendarIntegrationPageReturn<T> {
  const api = useApi();
  const toastError = useToastError();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [calendars, setCalendars] = useState<T[]>([]);
  const [isLoadingCalendars, setIsLoadingCalendars] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const [showCreatePromptDialog, setShowCreatePromptDialog] = useState(false);
  const [isUpdatingPrompt, setIsUpdatingPrompt] = useState(false);
  const [promptName, setPromptName] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isCreatingFakeEvent, setIsCreatingFakeEvent] = useState(false);
  const [showFakeEventDialog, setShowFakeEventDialog] = useState(false);

  useEffect(() => {
    const fetchPromptName = async (): Promise<void> => {
      if (config.integration?.promptId) {
        try {
          const { data: promptData } = await api.get<PromptDTO>(
            `/prompt/${config.integration.promptId}`
          );
          setPromptName(promptData.title || null);
        } catch {
          setPromptName(null);
        }
      } else {
        setPromptName(null);
      }
    };

    fetchPromptName();
  }, [config.integration?.promptId, api]);

  const fetchCalendars = useCallback(async (): Promise<void> => {
    try {
      setIsLoadingCalendars(true);
      const { data } = await api.get<T[]>(`${config.apiBasePath}/calendars`);
      setCalendars(data);
    } catch {
      toastError();
    } finally {
      setIsLoadingCalendars(false);
    }
  }, [api, config.apiBasePath, toastError]);

  const createIntegration = useCallback(async (code: string): Promise<void> => {
    try {
      await api.post(config.apiBasePath, { code });
      await config.refetch();
      await fetchCalendars();

      toast({
        title: t(`${config.i18nPrefix}.success.title`),
        description: t(`${config.i18nPrefix}.success.description`),
      });
    } catch {
      toastError();
    }
  }, [api, config, fetchCalendars, t, toast, toastError]);

  const handleConnect = (): void => {
    const clientId = import.meta.env[config.oauthEnvKey] as string | undefined;
    if (!clientId) {
      toastError();
      return;
    }

    const params = new URLSearchParams(config.oauthParams(clientId));
    window.location.href = `${config.oauthUrl}?${params.toString()}`;
  };

  const handleUpdateCalendar = async (calendarId: string): Promise<void> => {
    const calendar = calendars.find((cal) => cal.id === calendarId);
    if (!calendar) return;

    try {
      await api.put(`${config.apiBasePath}/selected-calendar`, {
        calendarId: calendar.id,
        calendarName: calendar.name,
      });

      await config.refetch();

      toast({
        title: t(`${config.i18nPrefix}.calendar.updated`),
        description: t(`${config.i18nPrefix}.calendar.updatedDescription`),
      });
    } catch {
      toastError();
    }
  };

  const handleDisconnect = async (): Promise<void> => {
    try {
      setIsDisconnecting(true);
      setShowDisconnectDialog(false);
      await api.delete(config.apiBasePath);

      await config.refetch();
      setCalendars([]);

      toast({
        title: t(`${config.i18nPrefix}.disconnect.title`),
        description: t(`${config.i18nPrefix}.disconnect.description`),
      });
    } catch {
      toastError();
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleUpdatePrompt = async (prompt: PromptDTO | null): Promise<void> => {
    try {
      setIsUpdatingPrompt(true);
      await api.put(`${config.apiBasePath}/prompt`, {
        promptId: prompt?.id || null,
      });

      setPromptName(prompt?.title || null);
      await config.refetch();

      toast({
        title: t(`${config.i18nPrefix}.prompt.updated`),
        description: t(`${config.i18nPrefix}.prompt.updatedDescription`),
      });

      setShowPromptDialog(false);
    } catch {
      toastError();
    } finally {
      setIsUpdatingPrompt(false);
    }
  };

  const handleSync = async (): Promise<void> => {
    const syncKey = config.syncSuccessI18nKey ?? "sync.forceSuccess";
    try {
      setIsSyncing(true);
      await api.post(`${config.apiBasePath}/sync`);

      if (config.refetchAfterSync) {
        await config.refetch();
      }

      toast({
        title: t(`${config.i18nPrefix}.${syncKey}.title`),
        description: t(`${config.i18nPrefix}.${syncKey}.description`),
      });
    } catch {
      toastError();
    } finally {
      setIsSyncing(false);
    }
  };

  const handleCreateFakeEvent = async (emails: string[]): Promise<void> => {
    try {
      setIsCreatingFakeEvent(true);
      const { data } = await api.post(`${config.apiBasePath}/fake-event`, { emails });

      toast({
        title: data.emailSent
          ? t("team.integrations.calendar.fakeEvent.success.title")
          : t("team.integrations.calendar.fakeEvent.warning.title"),
        description:
          data.message ||
          t("team.integrations.calendar.fakeEvent.success.description"),
        variant: data.emailSent ? "default" : "destructive",
      });

      setShowFakeEventDialog(false);
    } catch {
      toastError();
    } finally {
      setIsCreatingFakeEvent(false);
    }
  };

  useEffect(() => {
    const code = searchParams.get("code");
    if (code) {
      createIntegration(code);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, createIntegration]);

  useEffect(() => {
    if (config.integration) {
      fetchCalendars();
    }
  }, [config.integration, fetchCalendars]);

  return {
    calendars,
    isLoadingCalendars,
    isDisconnecting,
    showDisconnectDialog,
    setShowDisconnectDialog,
    showPromptDialog,
    setShowPromptDialog,
    showCreatePromptDialog,
    setShowCreatePromptDialog,
    isUpdatingPrompt,
    promptName,
    isSyncing,
    isCreatingFakeEvent,
    showFakeEventDialog,
    setShowFakeEventDialog,
    fetchCalendars,
    handleConnect,
    handleDisconnect,
    handleUpdateCalendar,
    handleUpdatePrompt,
    handleSync,
    handleCreateFakeEvent,
  };
}
